import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import axios from "axios";
import { attrConditionalDisplayColObj, attrGroupLinkedTables, attrGrpTableStrColumnsObj } from "components/PimDashboard/data";
import { PimDashboardState } from "context/PimDashboardContext";
import { inputTypes } from "data/globalData";
import { attrGrpAttrAssocObj, attrValAssocObj } from "../../DesignerAttributes/data";
import RenderAttributeInputs from "../RenderAttributes/RenderAttributeInputs";
import RenderConditionalAttributeInputs from "../RenderConditionalAttributes/RenderConditionalAttributeInputs";
import { removeDropdownOptionsBasedonDependency } from "../RenderAttributes/RenderAttributeInputs";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";

const cloneStyleDetails = (data = {}) =>
	Object.keys(data).reduce((acc, key) => {
		const value = data[key];
		acc[key] = Array.isArray(value) ? [...value] : value && typeof value === "object" ? { ...value } : value;
		return acc;
	}, {});

const getNormalizedSuggestedValues = (attr, rawValue, workingData, allAttributes) => {
	if (rawValue === null || rawValue === undefined) {
		return { accepted: false, value: null };
	}

	if (attr?.input_type === inputTypes.SELECT.key || attr?.input_type === inputTypes.MULTIPLE_SELECT.key) {
		const allowedOptions = removeDropdownOptionsBasedonDependency(attr, workingData, attr?.[attrValAssocObj], allAttributes);
		const allowedValues = new Set((allowedOptions?.list || []).map((row) => row?.key));

		if (attr?.input_type === inputTypes.MULTIPLE_SELECT.key) {
			const values = Array.isArray(rawValue)
				? rawValue
				: typeof rawValue === "string"
					? rawValue
							.split(",")
							.map((row) => row?.trim())
							.filter(Boolean)
					: [];

			const filteredValues = values.filter((row) => allowedValues.has(row));
			return {
				accepted: filteredValues.length > 0,
				value: filteredValues,
				hasRejectedValues: filteredValues.length !== values.length,
			};
		}

		return {
			accepted: allowedValues.has(rawValue),
			value: allowedValues.has(rawValue) ? rawValue : "",
		};
	}

	return {
		accepted: true,
		value: rawValue,
	};
};

const SuggestAttributesModal = ({ open, onClose, baseData, setBaseData, mainImageFile }) => {
	const { attrGroupsWithAttr, allAttributes } = PimDashboardState();
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [errObj, setErrObj] = useState({});
	const [suggestedData, setSuggestedData] = useState(() => cloneStyleDetails(baseData));
	const [suggestedAttrKeys, setSuggestedAttrKeys] = useState([]);
	const [clearedSuggestions, setClearedSuggestions] = useState([]);

	const suggestionGroups = useMemo(() => {
		return Object.keys(attrGroupsWithAttr)
			.reduce((acc, key) => {
				const group = attrGroupsWithAttr[key];
				if ([group?.[attrGrpTableStrColumnsObj.linked_table.key]].includes(attrGroupLinkedTables.style_details.key)) {
					const filteredColumns = (group?.[attrGrpAttrAssocObj] || []).filter((column) =>
						suggestedAttrKeys.includes(column?.key),
					);
					if (filteredColumns.length) acc.push({ ...group, [attrGrpAttrAssocObj]: filteredColumns });
				}
				return acc;
			}, [])
			.sort((a, b) => (a?.sort_no || 0) - (b?.sort_no || 0));
	}, [attrGroupsWithAttr, suggestedAttrKeys]);

	useEffect(() => {
		if (!open) return;

		const loadSuggestions = async () => {
			setLoading(true);
			setError("");
			setErrObj({});
			setClearedSuggestions([]);
			setSuggestedAttrKeys([]);

			const workingData = cloneStyleDetails(baseData);

			try {
				const formData = new FormData();
				formData.append("file", mainImageFile);
				formData.append("attributes", JSON.stringify(baseData[attrGroupLinkedTables.style.key]));

				const { data } = await pythonVmInstance.post("/api/generate_relook_attributes", formData, {
					headers: { "Content-Type": "multipart/form-data" },
				});

				const extractedAttributes = data?.result?.attributes || {};
				const extractedAttrList = allAttributes.filter(
					(attr) =>
						[attrGroupLinkedTables.style_details.key].includes(attr?.linked_table) &&
						Object.prototype.hasOwnProperty.call(extractedAttributes, attr?.key),
				);
				const extractedKeys = extractedAttrList.map((attr) => attr.key);
				const normalizedResults = {};

				for (let pass = 0; pass < 3; pass += 1) {
					extractedAttrList.forEach((attr) => {
						const extractedValue = extractedAttributes?.[attr?.key]?.value;
						const normalized = getNormalizedSuggestedValues(attr, extractedValue, workingData, allAttributes);

						normalizedResults[attr.key] = normalized;

						if (normalized.accepted) {
							workingData[attr.linked_table] = {
								...(workingData[attr.linked_table] || {}),
								[attr.key]: normalized.value,
							};
						}
					});
				}

				const rejectedValues = extractedAttrList.reduce((acc, attr) => {
					const extractedValue = extractedAttributes?.[attr?.key]?.value;
					const normalized = normalizedResults[attr.key];

					if (!normalized?.accepted || normalized?.hasRejectedValues) {
						acc.push({
							key: attr.key,
							name: attr.name,
							value: extractedValue,
						});
					}

					return acc;
				}, []);

				setSuggestedData(workingData);
				setSuggestedAttrKeys(extractedKeys);
				setClearedSuggestions(
					rejectedValues.filter((row, index, arr) => arr.findIndex((item) => item.key === row.key) === index),
				);
			} catch (err) {
				setError(parseErrorMessage(err));
				setSuggestedData(cloneStyleDetails(baseData));
			} finally {
				setLoading(false);
			}
		};

		loadSuggestions();
	}, [open, baseData, mainImageFile, allAttributes]);

	const handleApply = async () => {
		setSaving(true);
		setBaseData(suggestedData);
		setSaving(false);
		onClose();
	};

	return (
		<Dialog fullWidth maxWidth="xl" open={open} onClose={onClose}>
			<DialogTitle>Suggested Attributes</DialogTitle>
			<DialogContent dividers>
				{loading ? (
					<Box minHeight="240px" display="flex" alignItems="center" justifyContent="center" columnGap={1.5}>
						<CircularProgress size={24} />
						<Typography variant="body2">Extracting attributes from the main image...</Typography>
					</Box>
				) : error ? (
					<Alert severity="error">{error}</Alert>
				) : suggestionGroups.length === 0 ? (
					<Alert severity="info">No suggested attributes were found for the selected main image.</Alert>
				) : (
					<Box width="100%">
						{clearedSuggestions.length > 0 && (
							<Alert severity="warning" sx={{ mb: 2 }}>
								Some extracted values were cleared because they are not allowed in the current attribute values:{" "}
								{clearedSuggestions.map((row) => row.name).join(", ")}
							</Alert>
						)}
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Review the suggested attributes, update anything you want, then apply them to Create Style.
						</Typography>
						{suggestionGroups.map((group) => (
							<Box width="100%" pt={1.5} key={group?.group_id || group?.name}>
								<Typography variant="h6">{group[attrGrpTableStrColumnsObj.name.key]}</Typography>
								{group?.data?.[attrConditionalDisplayColObj.key] ? (
									<RenderConditionalAttributeInputs
										columns={[...group[attrGrpAttrAssocObj]]}
										setData={setSuggestedData}
										data={suggestedData}
										attrGrp={group}
										errorObj={errObj}
										setErrorObj={setErrObj}
										itemWidth="32%"
									/>
								) : (
									<RenderAttributeInputs
										columns={[...group[attrGrpAttrAssocObj]]}
										setData={setSuggestedData}
										data={suggestedData}
										attrGrp={group}
										errObj={errObj}
										setErrorObj={setErrObj}
										itemWidth="32%"
									/>
								)}
							</Box>
						))}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={onClose}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleApply} disabled={loading || !!error || suggestionGroups.length === 0}>
					{saving ? "Applying..." : "Use Values"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SuggestAttributesModal;
